/**
 * Onde a ficha do mapa (PopupHost) pode ficar.
 *
 * No celular há uma coluna de botões na borda direita do mapa (voltar à
 * Amazônia, "⋯", últimos focos, previsão). A ficha fica por cima dos
 * botões do mapa — senão a legenda a esconderia —, então ela é que tem de
 * desviar da coluna: a coluna é área reservada para o enquadramento, para a
 * largura máxima e para o arrasto. Geometria pura, em px da janela.
 *
 * O trilho de camadas, na borda esquerda (ui/TrilhoDeCamadas), só é reservado
 * quando sobra espaço (celular deitado): em pé, tirar os dois lados deixaria a
 * ficha estreita demais, e ela pode cobrir o trilho — a pessoa fecha a ficha e
 * volta a ele (`medirTrilho`).
 */
import { CONSULTA_CELULAR } from '../ui/layoutCelular';

/** Retângulo em px da janela (como o `getBoundingClientRect`). */
export interface Caixa {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Folga entre a ficha e a borda do mapa ao enquadrar. */
export const MARGEM_PX = 16;
/** Folga entre a ficha e a coluna de botões (e a borda oposta, quando o espaço é curto). */
export const FOLGA_DA_COLUNA_PX = 8;

/** Botões que a ficha não pode cobrir: os marcados e o do rastreio (tracking/TrackingFloat). */
export const SELETOR_DA_COLUNA = '[data-coluna-do-mapa], [data-tracking-tab]';

/** A mesma regra do `useIsMobile` (ui/useMediaQuery). */
export { CONSULTA_CELULAR };

/** O trilho de camadas do celular. */
export const SELETOR_DO_TRILHO = '[data-trilho-do-mapa]';

/**
 * Espaço mínimo (px) entre o trilho e a coluna para o trilho virar área
 * reservada: a ficha completa do celular (~360 px) e as folgas. Em pé (360 a
 * 412 px de largura) nunca sobra; deitado (667 px ou mais), sempre.
 */
export const ESPACO_PARA_RESERVAR_O_TRILHO_PX = 384;

/** O retângulo que envolve os botões da coluna; null fora do celular ou sem botões. */
export function medirColuna(raiz: ParentNode = document): Caixa | null {
  if (typeof window === 'undefined' || !window.matchMedia?.(CONSULTA_CELULAR).matches) return null;
  let coluna: Caixa | null = null;
  for (const el of raiz.querySelectorAll<HTMLElement>(SELETOR_DA_COLUNA)) {
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    coluna = coluna
      ? { left: Math.min(coluna.left, r.left), top: Math.min(coluna.top, r.top), right: Math.max(coluna.right, r.right), bottom: Math.max(coluna.bottom, r.bottom) }
      : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }
  return coluna;
}

/**
 * O retângulo do trilho de camadas quando ele é área reservada: no celular e
 * com pelo menos `ESPACO_PARA_RESERVAR_O_TRILHO_PX` entre ele e a coluna (ou a
 * borda direita do mapa). Senão null: a ficha pode cobri-lo.
 */
export function medirTrilho(mapa: Caixa, coluna: Caixa | null, raiz: ParentNode = document): Caixa | null {
  if (typeof window === 'undefined' || !window.matchMedia?.(CONSULTA_CELULAR).matches) return null;
  const r = raiz.querySelector<HTMLElement>(SELETOR_DO_TRILHO)?.getBoundingClientRect();
  if (!r || r.width <= 0 || r.height <= 0) return null;
  const trilho: Caixa = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  return trilhoReservado(trilho, mapa, coluna);
}

/** A regra de `medirTrilho`, sem o DOM: o trilho, se o espaço até a coluna permite reservá-lo. */
export function trilhoReservado(trilho: Caixa | null, mapa: Caixa, coluna: Caixa | null): Caixa | null {
  if (!trilho) return null;
  const direita = coluna ? coluna.left : mapa.right;
  return direita - trilho.right >= ESPACO_PARA_RESERVAR_O_TRILHO_PX ? trilho : null;
}

/** A faixa vertical [top, bottom] passa pela altura da coluna (com a folga)? */
export function naAlturaDaColuna(top: number, bottom: number, coluna: Caixa | null): boolean {
  return !!coluna && top < coluna.bottom + FOLGA_DA_COLUNA_PX && bottom > coluna.top - FOLGA_DA_COLUNA_PX;
}

/**
 * Largura máxima da ficha para caber ao lado da coluna, com a folga dos dois
 * lados; com o trilho reservado (deitado), entre ele e a coluna.
 */
export function larguraAoLadoDaColuna(mapa: Caixa, coluna: Caixa | null, trilho: Caixa | null = null): number | null {
  if (!coluna && !trilho) return null;
  const esquerda = trilho ? trilho.right : mapa.left;
  const direita = coluna ? coluna.left : mapa.right;
  return Math.max(160, Math.floor(direita - esquerda - 2 * FOLGA_DA_COLUNA_PX));
}

/**
 * Quanto o mapa tem de andar (px) para a ficha caber: positivo em x leva a
 * ficha para a esquerda; em y, para cima. Como o `panIntoView` do OpenLayers,
 * com a coluna de botões a mais: se a ficha, já na altura final, passa pela
 * altura da coluna, a borda direita útil é a da coluna, e ela tem prioridade
 * sobre a folga da esquerda. O trilho reservado (`medirTrilho`, deitado) faz o
 * mesmo papel na esquerda: a borda esquerda útil é a dele.
 */
export function deslocamentoParaCaber(ficha: Caixa, mapa: Caixa, coluna: Caixa | null, trilho: Caixa | null = null): [number, number] {
  let dy = 0;
  if (ficha.top < mapa.top + MARGEM_PX) dy = ficha.top - (mapa.top + MARGEM_PX);
  else if (ficha.bottom > mapa.bottom - MARGEM_PX) dy = ficha.bottom - (mapa.bottom - MARGEM_PX);

  let direita = mapa.right - MARGEM_PX;
  if (naAlturaDaColuna(ficha.top - dy, ficha.bottom - dy, coluna)) direita = Math.min(direita, coluna!.left - FOLGA_DA_COLUNA_PX);
  // A borda esquerda útil: a do mapa, com a folga (que pode ceder até FOLGA_DA_COLUNA_PX), ou a do trilho.
  const aoLadoDoTrilho = naAlturaDaColuna(ficha.top - dy, ficha.bottom - dy, trilho);
  const esquerda = aoLadoDoTrilho ? trilho!.right + FOLGA_DA_COLUNA_PX : mapa.left + MARGEM_PX;
  const esquerdaMinima = aoLadoDoTrilho ? esquerda : mapa.left + FOLGA_DA_COLUNA_PX;

  let dx = 0;
  if (ficha.right > direita) {
    // Sem espaço para as duas folgas, a da esquerda cede até FOLGA_DA_COLUNA_PX.
    dx = Math.min(ficha.right - direita, Math.max(0, ficha.left - esquerdaMinima));
  } else if (ficha.left < esquerda) {
    dx = Math.max(ficha.left - esquerda, ficha.right - direita);
  }
  return [dx, dy];
}

// ---------------------------------------------------------------------------
// Ficha ao lado de uma área a evitar (desktop)
// ---------------------------------------------------------------------------
//
// Uma ficha pode declarar uma área que não deve cobrir — a do sistema de
// nuvens selecionado (tracking). Aí ela abre FORA do retângulo dessa área, do
// lado com mais espaço, e fica ligada ao ponto pela linha da ficha móvel. Só
// vale para quem declara a área (`evitar`, em `popupControl`): as outras
// fichas continuam abrindo sobre o ponto. No celular não vale: a tela não tem
// para onde a ficha correr.

export interface Tamanho {
  largura: number;
  altura: number;
}

export type Lado = 'direita' | 'esquerda' | 'acima' | 'abaixo' | 'canto';

export interface Colocacao {
  caixa: Caixa;
  lado: Lado;
  /** Área (px²) da ficha sobre a área a evitar: zero, a não ser no caso-limite. */
  cobre: number;
  /** Área (px²) da ficha sobre o que já está no mapa (painéis, botões, legenda). */
  ocupado: number;
}

/** Distância entre a ficha e a área a evitar. */
export const FOLGA_DA_AREA_PX = 14;

/** Área (px²) da interseção de dois retângulos. */
export function intersecao(a: Caixa, b: Caixa): number {
  const l = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return l > 0 && h > 0 ? l * h : 0;
}

/** Sobreposição (px²) com um painel que não vale mover a ficha: um canto de botão. */
const TOQUE_DESPREZIVEL_PX2 = 400;

const prender = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

/**
 * Onde pôr a ficha para não cobrir `evitar`, dentro de `livre` (a parte útil
 * do mapa), de preferência fora das áreas `ocupadas` (painéis e botões sobre
 * o mapa).
 * - Os quatro lados são tentados; ao longo do lado a ficha se centra na área
 *   e é presa a `livre`. Serve o lado em que ela cabe sem tocar a área.
 * - Centrada, ela cobriria um painel (a legenda no pé do mapa, o cartão do
 *   rastreio)? Desliza ao longo do lado, sem perder o centro da área de vista
 *   (o centro fica na altura da ficha, nos lados; na largura, acima e abaixo),
 *   até o lugar que cobre menos; no empate, o mais perto do centrado.
 * - Entre os que servem: o que cobre menos as áreas ocupadas; no empate, o de
 *   mais espaço livre.
 * - CASO-LIMITE — a área é maior que o espaço (sistema gigante, ou zoom
 *   alto): nenhum lado serve. A ficha vai para o canto de `livre` que cobre a
 *   MENOR parte da área (no empate, o que cobre menos as ocupadas). O zoom do
 *   usuário não é mexido.
 */
export function colocarFora(evitar: Caixa, ficha: Tamanho, livre: Caixa, ocupadas: readonly Caixa[] = [], folga: number = FOLGA_DA_AREA_PX): Colocacao {
  const { largura: w, altura: h } = ficha;
  const cx = (evitar.left + evitar.right) / 2;
  const cy = (evitar.top + evitar.bottom) / 2;
  const em = (left: number, top: number): Caixa => {
    const l = prender(left, livre.left, livre.right - w);
    const t = prender(top, livre.top, livre.bottom - h);
    return { left: l, top: t, right: l + w, bottom: t + h };
  };
  const sobreOcupadas = (c: Caixa) => ocupadas.reduce((soma, o) => soma + intersecao(c, o), 0);

  /**
   * O melhor lugar ao longo de um lado: a posição centrada e as que encostam
   * nas bordas de cada painel e da parte útil, sem perder o centro da área.
   */
  const aoLongo = (fixo: number, centrado: number, vertical: boolean): Caixa => {
    const tam = vertical ? h : w;
    const centro = vertical ? cy : cx;
    const lo = vertical ? livre.top : livre.left;
    const hi = vertical ? livre.bottom : livre.right;
    const candidatos = [centrado, lo, hi - tam];
    for (const o of ocupadas) {
      const [a, b] = vertical ? [o.top, o.bottom] : [o.left, o.right];
      candidatos.push(a - tam - FOLGA_DA_COLUNA_PX, b + FOLGA_DA_COLUNA_PX);
    }
    const caixa = (pos: number) => (vertical ? em(fixo, pos) : em(pos, fixo));
    const inicioCentrado = vertical ? caixa(centrado).top : caixa(centrado).left;
    return candidatos
      .map(caixa)
      .filter((c) => (vertical ? c.top <= centro && centro <= c.bottom : c.left <= centro && centro <= c.right))
      // Um canto de botão (até ~20×20 px) não vale deslizar a ficha quase a altura inteira.
      .map((c) => ({ c, ocupado: sobreOcupadas(c) <= TOQUE_DESPREZIVEL_PX2 ? 0 : sobreOcupadas(c), longe: Math.abs((vertical ? c.top : c.left) - inicioCentrado) }))
      .sort((a, b) => a.ocupado - b.ocupado || a.longe - b.longe)[0]?.c ?? caixa(centrado);
  };

  const lados: { lado: Lado; caixa: Caixa; espaco: number }[] = [
    { lado: 'direita', caixa: aoLongo(evitar.right + folga, cy - h / 2, true), espaco: livre.right - evitar.right },
    { lado: 'esquerda', caixa: aoLongo(evitar.left - folga - w, cy - h / 2, true), espaco: evitar.left - livre.left },
    { lado: 'acima', caixa: aoLongo(evitar.top - folga - h, cx - w / 2, false), espaco: evitar.top - livre.top },
    { lado: 'abaixo', caixa: aoLongo(evitar.bottom + folga, cx - w / 2, false), espaco: livre.bottom - evitar.bottom },
  ];
  const servem = lados
    .filter((l) => intersecao(l.caixa, evitar) === 0)
    .map((l) => ({ ...l, ocupado: sobreOcupadas(l.caixa) }))
    .sort((a, b) => a.ocupado - b.ocupado || b.espaco - a.espaco);
  if (servem.length) return { caixa: servem[0]!.caixa, lado: servem[0]!.lado, cobre: 0, ocupado: servem[0]!.ocupado };

  const cantos = [em(livre.right - w, livre.top), em(livre.left, livre.top), em(livre.right - w, livre.bottom - h), em(livre.left, livre.bottom - h)]
    .map((caixa) => ({ caixa, cobre: intersecao(caixa, evitar), ocupado: sobreOcupadas(caixa) }))
    .sort((a, b) => a.cobre - b.cobre || a.ocupado - b.ocupado);
  return { caixa: cantos[0]!.caixa, lado: 'canto', cobre: cantos[0]!.cobre, ocupado: cantos[0]!.ocupado };
}

/**
 * A forma da ficha: `completa`, ou `compacta` (a do celular, com "Mais
 * detalhes"), bem menor. No desktop a completa mede 338×533 px, e com o painel
 * lateral aberto um sistema médio já não deixa lado livre para ela.
 */
export type Forma = 'completa' | 'compacta';

/**
 * Serve a colocação? Não cobre a área a evitar nem o que já está sobre o mapa
 * (o cartão do rastreio, a legenda). É o que decide passar da ficha completa
 * à compacta: só quando a completa não tem lugar limpo.
 */
export function lugarLimpo(c: Colocacao): boolean {
  return c.cobre === 0 && c.ocupado === 0;
}

/**
 * Entre a colocação da ficha completa e a da compacta: primeiro, não cobrir a
 * área a evitar; depois, não cobrir o que está sobre o mapa; no empate, a
 * completa. Só se nem a compacta couber vale o canto que cobre menos.
 */
export function escolherForma(completa: Colocacao, compacta: Colocacao): Forma {
  if (completa.cobre !== compacta.cobre) return completa.cobre < compacta.cobre ? 'completa' : 'compacta';
  return compacta.ocupado < completa.ocupado ? 'compacta' : 'completa';
}

/** A ficha, presa à parte útil do mapa: o lugar mais perto do atual em que ela cabe inteira. */
export function caberEm(ficha: Caixa, livre: Caixa): Caixa {
  const w = ficha.right - ficha.left;
  const h = ficha.bottom - ficha.top;
  const left = prender(ficha.left, livre.left, livre.right - w);
  const top = prender(ficha.top, livre.top, livre.bottom - h);
  return { left, top, right: left + w, bottom: top + h };
}

/** A ficha cabe inteira na parte útil do mapa? (com 1 px de tolerância) */
export function cabe(ficha: Caixa, livre: Caixa): boolean {
  return ficha.left >= livre.left - 1 && ficha.top >= livre.top - 1 && ficha.right <= livre.right + 1 && ficha.bottom <= livre.bottom + 1;
}

/**
 * Onde a linha de ligação encosta na ficha: o ponto da BORDA dela mais perto
 * do ponto do mapa — nunca por dentro da ficha. Com o ponto dentro da ficha
 * (ela o cobre: caso-limite, ou posta ali pelo usuário), não há linha: null.
 */
export function pontoNaBorda(ficha: Caixa, ponto: { x: number; y: number }): { x: number; y: number } | null {
  const dentro = ponto.x > ficha.left && ponto.x < ficha.right && ponto.y > ficha.top && ponto.y < ficha.bottom;
  if (dentro) return null;
  return { x: Math.min(Math.max(ponto.x, ficha.left), ficha.right), y: Math.min(Math.max(ponto.y, ficha.top), ficha.bottom) };
}

/**
 * A ficha, onde está, precisa sair? Só se cobre a área a evitar: a posição
 * que o usuário escolheu (ou a da última colocação) fica enquanto não cobrir
 * — a ficha não pula a cada quadro do player.
 */
export function precisaSair(ficha: Caixa, evitar: Caixa): boolean {
  return intersecao(ficha, evitar) > 0;
}

/** Um lugar novo só vale a troca se cobrir pelo menos um quinto a menos que o atual. */
export const GANHO_MINIMO = 0.2;

/**
 * No caso-limite a ficha já está no melhor canto e continua cobrindo: mexer de
 * novo só se outro lugar cobrir bem menos (um quinto a menos), senão ela
 * oscilaria entre cantos a cada quadro.
 */
export function valeMudar(atual: Caixa, nova: Colocacao, evitar: Caixa): boolean {
  const agora = intersecao(atual, evitar);
  if (agora === 0) return false;
  return nova.cobre === 0 || nova.cobre < agora * (1 - GANHO_MINIMO);
}
